import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import {
  clearModuleFilters,
  setModuleFilters,
} from "../filterPersistenceSlice";
import {
  activeFilterCount as selectActiveFilterCount,
  hasActiveFilters as selectHasActiveFilters,
  selectModuleFilters,
} from "../filterPersistenceSelectors";
import {
  buildUrlSearchParamsFromFilters,
  moduleFiltersAreEqual,
  parseUrlSearchParamsToFilters,
  resolveFiltersWithPriority,
} from "../filterUrlSync";

function sanitizeModuleFilters(filters) {
  const next = {};

  Object.entries(filters).forEach(([key, value]) => {
    if (value === null || value === undefined || value === "") {
      return;
    }
    next[key] = value;
  });

  return next;
}

export function usePersistentFilters(moduleKey, options = {}) {
  const {
    defaultFilters = {},
    urlConfig = {},
    syncUrl = true,
  } = options;

  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const persistedFilters = useSelector((state) =>
    selectModuleFilters(state, moduleKey)
  );
  const hasActive = useSelector((state) =>
    selectHasActiveFilters(state, moduleKey)
  );
  const filterCount = useSelector((state) =>
    selectActiveFilterCount(state, moduleKey)
  );

  const [isInitialized, setIsInitialized] = useState(false);
  const skipUrlSyncRef = useRef(false);
  const lastAppliedFiltersRef = useRef({});

  const urlFilters = useMemo(
    () => parseUrlSearchParamsToFilters(searchParams, urlConfig),
    [searchParams, urlConfig]
  );

  const resolvedFilters = useMemo(
    () =>
      resolveFiltersWithPriority({
        urlFilters,
        persistedFilters,
        defaultFilters,
      }),
    [urlFilters, persistedFilters, defaultFilters]
  );

  const filters = resolvedFilters.filters;

  useLayoutEffect(() => {
    if (isInitialized) {
      return;
    }

    lastAppliedFiltersRef.current = filters;

    if (!moduleFiltersAreEqual(filters, persistedFilters)) {
      dispatch(
        setModuleFilters({
          moduleKey,
          filters,
        })
      );
    }

    if (syncUrl && resolvedFilters.source === "persisted" && Object.keys(filters).length > 0) {
      skipUrlSyncRef.current = true;
      const params = buildUrlSearchParamsFromFilters(filters, urlConfig, searchParams);
      setSearchParams(params, { replace: true });
    }

    setIsInitialized(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleKey]);

  useLayoutEffect(() => {
    if (!isInitialized) {
      return;
    }

    if (skipUrlSyncRef.current) {
      skipUrlSyncRef.current = false;
      return;
    }

    if (moduleFiltersAreEqual(urlFilters, lastAppliedFiltersRef.current)) {
      return;
    }

    lastAppliedFiltersRef.current = urlFilters;

    dispatch(
      setModuleFilters({
        moduleKey,
        filters: urlFilters,
      })
    );
  }, [dispatch, isInitialized, moduleKey, urlFilters]);

  const syncAllSources = useCallback(
    (nextFilters) => {
      const sanitized = sanitizeModuleFilters(nextFilters);
      lastAppliedFiltersRef.current = sanitized;

      dispatch(
        setModuleFilters({
          moduleKey,
          filters: sanitized,
        })
      );

      if (!syncUrl) {
        return;
      }

      skipUrlSyncRef.current = true;
      const params = buildUrlSearchParamsFromFilters(sanitized, urlConfig, searchParams);
      setSearchParams(params, { replace: true });
    },
    [dispatch, moduleKey, searchParams, setSearchParams, syncUrl, urlConfig]
  );

  const setFilters = useCallback(
    (nextFilters) => {
      syncAllSources(nextFilters);
    },
    [syncAllSources]
  );

  const updateFilter = useCallback(
    (key, value) => {
      const current = { ...lastAppliedFiltersRef.current };

      if (value === null || value === undefined || value === "") {
        delete current[key];
      } else {
        current[key] = value;
      }

      syncAllSources(current);
    },
    [syncAllSources]
  );

  const removeFilter = useCallback(
    (key) => {
      const current = { ...lastAppliedFiltersRef.current };
      delete current[key];
      syncAllSources(current);
    },
    [syncAllSources]
  );

  const clearFilters = useCallback(() => {
    dispatch(clearModuleFilters({ moduleKey }));
    lastAppliedFiltersRef.current = {};

    if (!syncUrl) {
      return;
    }

    skipUrlSyncRef.current = true;
    const params = buildUrlSearchParamsFromFilters({}, urlConfig, searchParams);
    setSearchParams(params, { replace: true });
  }, [dispatch, moduleKey, searchParams, setSearchParams, syncUrl, urlConfig]);

  return {
    filters,
    setFilters,
    updateFilter,
    removeFilter,
    clearFilters,
    hasActiveFilters: hasActive,
    activeFilterCount: filterCount,
    isInitialized,
    initialSource: resolvedFilters.source,
  };
}
